import { prefersReducedMotion } from "@js/parts/prefersReducedMotion";

export default (config = {}) => ({
  max: config.max ?? 15,
  multiplier: config.multiplier ?? 0.05,
  reset: config.reset ?? true,
  origin: config.origin ?? "center center",
  mouseOriginEl: config.mouseOriginEl ?? null,
  originElement: config.originElement ?? null,

  // State
  targetTiltX: 0,
  targetTiltY: 0,
  easedTiltX: 0,
  easedTiltY: 0,
  animationFrame: null,
  isAnimating: false,
  isFirefox: false,
  mouseOriginElement: null,
  transformElement: null,

  init() {
    // Detect Firefox - it doesn't support backdrop-filter with 3D transforms
    this.isFirefox = navigator.userAgent.toLowerCase().includes("firefox");

    // If Firefox or user prefers reduced motion, skip tilt setup entirely
    if (this.isFirefox || prefersReducedMotion()) {
      return;
    }

    // Resolve element references
    this.mouseOriginElement = this.mouseOriginEl
      ? (typeof this.mouseOriginEl === 'string'
          ? document.querySelector(this.mouseOriginEl)
          : this.mouseOriginEl)
      : this.$el;

    this.transformElement = this.originElement
      ? (typeof this.originElement === 'string'
          ? this.$el.querySelector(this.originElement)
          : this.originElement)
      : this.$el;

    // Set initial styles on the element that will transform
    this.transformElement.style.transformOrigin = this.origin;
    this.transformElement.style.transition = "none";

    // Bind event listeners to the mouse origin element
    this.mouseOriginElement.addEventListener("mousemove", this.handleMouseMove.bind(this));
    this.mouseOriginElement.addEventListener("mouseenter", this.handleMouseEnter.bind(this));
    this.mouseOriginElement.addEventListener("mouseleave", this.handleMouseLeave.bind(this));
  },

  getValues(e) {
    const rect = this.mouseOriginElement.getBoundingClientRect();
    const width = rect.width;
    const height = rect.height;
    const left = rect.left;
    const top = rect.top;

    const percentageX = ((e.clientX - left) / width) * 100;
    const percentageY = ((e.clientY - top) / height) * 100;

    let tiltX = this.max / 2 - (percentageY / 100) * this.max;
    let tiltY = (percentageX / 100) * this.max - this.max / 2;

    return {
      tiltX: Math.round(tiltX * 100) / 100,
      tiltY: Math.round(tiltY * 100) / 100,
    };
  },

  updateTransforms() {
    const transforms = [
      `rotateX(${this.easedTiltX}deg)`,
      `rotateY(${this.easedTiltY}deg)`,
    ];

    this.transformElement.style.transform = transforms.join(" ");
  },

  animate() {
    // Apply easing formula (same as original)
    this.easedTiltX -= this.multiplier * (this.targetTiltX + this.easedTiltX);
    this.easedTiltY -= this.multiplier * (this.targetTiltY + this.easedTiltY);

    // Update DOM
    this.updateTransforms();

    // Continue animation if active
    if (this.isAnimating) {
      this.animationFrame = requestAnimationFrame(() => this.animate());
    }
  },

  startAnimation() {
    if (!this.isAnimating) {
      this.isAnimating = true;
      this.animationFrame = requestAnimationFrame(() => this.animate());
    }
  },

  stopAnimation() {
    this.isAnimating = false;
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
      this.animationFrame = 0;
    }
  },

  handleMouseMove(e) {
    const values = this.getValues(e);
    this.targetTiltX = values.tiltX;
    this.targetTiltY = values.tiltY;
    this.startAnimation();
  },

  handleMouseEnter() {
    this.transformElement.style.willChange = "transform";
    this.startAnimation();
  },

  handleMouseLeave() {
    this.transformElement.style.willChange = "auto";

    if (this.reset) {
      // Set target to zero for smooth reset
      this.targetTiltX = 0;
      this.targetTiltY = 0;

      // Keep animation running for smooth reset
      if (!this.isAnimating) {
        this.startAnimation();
      }

      // Stop animation after reset completes (500ms default)
      setTimeout(() => {
        if (this.targetTiltX === 0 && this.targetTiltY === 0) {
          this.stopAnimation();
        }
      }, 500);
    } else {
      this.stopAnimation();
    }
  },

  destroy() {
    this.stopAnimation();
    if (this.mouseOriginElement) {
      this.mouseOriginElement.removeEventListener("mousemove", this.handleMouseMove);
      this.mouseOriginElement.removeEventListener("mouseenter", this.handleMouseEnter);
      this.mouseOriginElement.removeEventListener("mouseleave", this.handleMouseLeave);
    }
  },
});
